import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateUserPayload = {
  email: string;
  full_name: string;
};

export type UserSummary = {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  /**
   * The built-in role. It is a column on the user, not a `roles` row, so it travels with the user
   * rather than in a separate access listing — there is no `GET /accesses` any more.
   */
  is_super_administrator: boolean;
  /** Names of the tenant-wide roles this user holds. Domain grants live on each domain's tab. */
  tenant_roles: string[];
  created_at: string;
};

/** One tenant-wide role grant, as returned by the `/v1/accesses` endpoints. */
export type TenantRoleGrant = {
  id: number;
  user_id: number;
  user_email: string;
  user_full_name: string | null;
  role_id: number;
  role_name: string;
  created_at: string;
};

/** Access-control payload returned for the logged-in user. */
export type UserAccess = {
  permissions: string[];
  /** Kebab-case tab names the user may view, in backend-defined order. */
  tabs: string[];
  /**
   * True when the logged-in user is a Super Administrator. They hold every permission, so
   * `permissions` alone cannot tell them apart from a role that happens to hold a lot — and only
   * they may grant or revoke the role on the Users screen.
   */
  is_super_administrator: boolean;
  /**
   * Permission code -> the domain ids it is restricted to, for codes held **only** through a
   * domain role. A code in `permissions` but absent here is unrestricted: held workspace-wide, or
   * held by a Super Administrator, so it applies to every domain including ones created later.
   *
   * Read it through `usePermissions().hasForDomain` rather than directly — `permissions` alone
   * reads as workspace-wide, which is what made a domain-scoped grant light up controls on every
   * domain the user could open.
   */
  // Optional: omitted by an older cached payload, and the guard tolerates that. Read it through
  // the selector, which defaults to an empty map -- no restrictions.
  domain_permissions?: Record<string, number[]>;
};

/** The logged-in user, including the access object the list/create endpoints do not return. */
export type LoggedInUser = UserSummary & {
  access: UserAccess;
};

export type UsersState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  users: UserSummary[];
  currentUser: LoggedInUser | null;
  currentUserStatus: "idle" | "loading" | "succeeded" | "failed";
  tenantRoleGrants: TenantRoleGrant[];
};

export const usersInitialState: UsersState = {
  status: STATUS.IDLE,
  error: null,
  users: [],
  currentUser: null,
  currentUserStatus: STATUS.IDLE,
  tenantRoleGrants: [],
};

function isUserSummary(value: unknown): value is UserSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.email === "string" &&
    (typeof row.full_name === "string" || row.full_name === null) &&
    typeof row.is_active === "boolean" &&
    typeof row.is_super_administrator === "boolean" &&
    Array.isArray(row.tenant_roles) &&
    row.tenant_roles.every((name) => typeof name === "string") &&
    typeof row.created_at === "string"
  );
}

function isDomainPermissions(value: unknown): value is Record<string, number[]> {
  // Absent is valid and means "nothing is restricted" -- the field is omitted for a Super
  // Administrator and for anyone whose grants are all workspace-wide, which is most payloads.
  if (value === undefined) {
    return true;
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every(
    (ids) => Array.isArray(ids) && ids.every((id) => typeof id === "number"),
  );
}

function isUserAccess(value: unknown): value is UserAccess {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const access = value as Record<string, unknown>;
  return (
    Array.isArray(access.permissions) &&
    access.permissions.every((permission) => typeof permission === "string") &&
    Array.isArray(access.tabs) &&
    access.tabs.every((tab) => typeof tab === "string") &&
    typeof access.is_super_administrator === "boolean" &&
    isDomainPermissions(access.domain_permissions)
  );
}

function isLoggedInUser(value: unknown): value is LoggedInUser {
  if (!isUserSummary(value)) {
    return false;
  }
  return isUserAccess((value as Record<string, unknown>).access);
}

function parseUsersResponse(data: unknown): UserSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isUserSummary)) {
    return null;
  }
  return data;
}

export const createUser = createAsyncThunk<
  UserSummary,
  CreateUserPayload,
  { rejectValue: string }
>("users/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isUserSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchCurrentUser = createAsyncThunk<
  LoggedInUser,
  void,
  { rejectValue: string }
>("users/fetchCurrentUser", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users/logged-in", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    if (!isLoggedInUser(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type UpdateCurrentUserPayload = {
  full_name: string;
};

export const updateCurrentUser = createAsyncThunk<
  LoggedInUser,
  UpdateCurrentUserPayload,
  { rejectValue: string }
>("users/updateCurrentUser", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users/logged-in", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isLoggedInUser(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchUsers = createAsyncThunk<
  UserSummary[],
  void,
  { rejectValue: string }
>("users/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const users = parseUsersResponse(data);
    if (!users) {
      return rejectWithValue("Unexpected response shape");
    }
    return users;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteUser = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("users/delete", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/users/${userId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return userId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/**
 * Clear the caller's own cached API responses.
 *
 * Returns nothing useful to the store — no slice state describes the cache — so the thunk reports
 * success or failure and nothing more.
 */
export const clearOwnCache = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>("users/clearOwnCache", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/users/logged-in/cache", { method: "POST" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** Clear another user's cached API responses. Workspace-level `user-cache-clear` on the backend. */
export const clearUserCache = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>("users/clearUserCache", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/users/${userId}/cache`, { method: "POST" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

function isTenantRoleGrant(value: unknown): value is TenantRoleGrant {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.user_id === "number" &&
    typeof row.user_email === "string" &&
    (typeof row.user_full_name === "string" || row.user_full_name === null) &&
    typeof row.role_id === "number" &&
    typeof row.role_name === "string" &&
    typeof row.created_at === "string"
  );
}

/**
 * Assign a tenant-wide role. Super-Administrator-only on the backend: handing out tenant roles is
 * how you hand out the workspace.
 */
export const assignTenantRole = createAsyncThunk<
  TenantRoleGrant,
  { userId: number; roleId: number },
  { rejectValue: string }
>("users/assignTenantRole", async ({ userId, roleId }, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/accesses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, role_id: roleId }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantRoleGrant(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchTenantRoleGrants = createAsyncThunk<
  TenantRoleGrant[],
  void,
  { rejectValue: string }
>("users/fetchTenantRoleGrants", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/accesses", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isTenantRoleGrant)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const revokeTenantRole = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("users/revokeTenantRole", async (accessId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/accesses/${accessId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return accessId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** Grant the built-in role. Only a Super Administrator may call this; the backend enforces it. */
export const grantSuperAdministrator = createAsyncThunk<
  UserSummary,
  number,
  { rejectValue: string }
>("users/grantSuperAdministrator", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/users/${userId}/super-administrator`, {
      method: "POST",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isUserSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const revokeSuperAdministrator = createAsyncThunk<
  UserSummary,
  number,
  { rejectValue: string }
>("users/revokeSuperAdministrator", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/users/${userId}/super-administrator`, {
      method: "DELETE",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isUserSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** Swap one row of `users` for the version the server just returned. */
function replaceUser(state: UsersState, updated: UserSummary): void {
  const index = state.users.findIndex((user) => user.id === updated.id);
  if (index !== -1) {
    state.users[index] = updated;
  }
}

const usersSlice = createSlice({
  name: "users",
  initialState: usersInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.users = [];
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.currentUserStatus = STATUS.LOADING;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUserStatus = STATUS.SUCCEEDED;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.currentUserStatus = STATUS.FAILED;
      })
      .addCase(updateCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(fetchTenantRoleGrants.fulfilled, (state, action) => {
        state.tenantRoleGrants = action.payload;
      })
      .addCase(fetchTenantRoleGrants.rejected, (state) => {
        state.tenantRoleGrants = [];
      })
      .addCase(assignTenantRole.fulfilled, (state, action) => {
        state.tenantRoleGrants.push(action.payload);
        const user = state.users.find((row) => row.id === action.payload.user_id);
        if (user && !user.tenant_roles.includes(action.payload.role_name)) {
          user.tenant_roles = [...user.tenant_roles, action.payload.role_name].sort();
        }
      })
      .addCase(revokeTenantRole.fulfilled, (state, action) => {
        const removed = state.tenantRoleGrants.find((grant) => grant.id === action.payload);
        state.tenantRoleGrants = state.tenantRoleGrants.filter(
          (grant) => grant.id !== action.payload,
        );
        if (!removed) {
          return;
        }
        const user = state.users.find((row) => row.id === removed.user_id);
        if (user) {
          user.tenant_roles = user.tenant_roles.filter((name) => name !== removed.role_name);
        }
      })
      .addCase(grantSuperAdministrator.fulfilled, (state, action) => {
        replaceUser(state, action.payload);
      })
      .addCase(revokeSuperAdministrator.fulfilled, (state, action) => {
        replaceUser(state, action.payload);
      });
  },
});

export const usersReducer = usersSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.users.currentUser;
export const selectCurrentUserStatus = (state: RootState) => state.users.currentUserStatus;

/** Tabs the logged-in user may view, already filtered and ordered by the backend. */
export const selectAccessibleTabs = createSelector(
  selectCurrentUser,
  (currentUser) => currentUser?.access.tabs ?? [],
);

/** API permissions for the logged-in user. Use to show/hide write actions on pages accessible to all users. */
export const selectAccessiblePermissions = createSelector(
  selectCurrentUser,
  (currentUser) => currentUser?.access.permissions ?? [],
);

/**
 * Domain restrictions on the logged-in user's permissions: code -> the domain ids it applies to.
 *
 * A code missing from this map is unrestricted. Pair it with `selectAccessiblePermissions` —
 * this map says *where* a held permission applies, never *whether* it is held.
 */
export const selectDomainRestrictedPermissions = createSelector(
  selectCurrentUser,
  (currentUser) => currentUser?.access.domain_permissions ?? {},
);

/** Every tenant-wide role grant in the workspace. */
export const selectTenantRoleGrants = (state: RootState) => state.users.tenantRoleGrants;

/** Tenant role grants keyed by user id, for resolving a row's grants and their ids. */
export const selectTenantRoleGrantsByUserId = createSelector(
  selectTenantRoleGrants,
  (grants) => {
    const byUser = new Map<number, TenantRoleGrant[]>();
    for (const grant of grants) {
      byUser.set(grant.user_id, [...(byUser.get(grant.user_id) ?? []), grant]);
    }
    return byUser;
  },
);

/** True when the logged-in user may grant or revoke the built-in role. */
export const selectIsSuperAdministrator = createSelector(
  selectCurrentUser,
  (currentUser) => currentUser?.access.is_super_administrator ?? false,
);

/** How many users hold the built-in role — the Users screen hides the last revoke button. */
export const selectSuperAdministratorCount = createSelector(
  (state: RootState) => state.users.users,
  (users) => users.filter((user) => user.is_super_administrator).length,
);
